import React from "react";
import { X, Trash2, Plus, Minus, MessageCircle } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useProducts } from "../context/ProductContext";
import { useAuth } from "../context/AuthContext";

function CartDrawer() {
  const {
    cartItems,
    isCartOpen,
    toggleCart,
    removeFromCart,
    updateQuantity,
    cartTotal,
  } = useCart();

  const { user } = useAuth();
  const { formatPrice, recordWhatsAppClick } = useProducts();

  if (!isCartOpen) return null;

  function handleCheckout() {
    recordWhatsAppClick(cartItems); // Keep the existing recordWhatsAppClick call

    // Generate WhatsApp link with cart contents
    const phoneNumber = "59160722786"; // Keep the existing phone number
    let message = "Hola, me gustaría realizar el siguiente pedido:\n\n";

    cartItems.forEach((item) => {
      const priceText = user ? formatPrice(item.price * item.quantity) : "";
      message += `- ${item.quantity}x ${item.name} ${priceText}\n`;
    });

    if (user) {
      message += `\n*Total: ${formatPrice(cartTotal)}*`;
    }
    message += "\n\nQuedo atento a su confirmación.";

    const url = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(
      message,
    )}`;
    window.open(url, "_blank");
  }

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
        onClick={toggleCart}
      />

      {/* Drawer */}
      <div className="relative w-full max-w-md bg-black h-full shadow-2xl border-l border-white/10 flex flex-col transform transition-transform duration-300">
        <div className="p-4 border-b border-white/10 flex items-center justify-between bg-black/50 backdrop-blur-md">
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <span className="text-red-500">Tu</span> Carrito
          </h2>
          <button
            onClick={toggleCart}
            className="p-2 text-white/60 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        <div className="flex-grow overflow-y-auto p-4 space-y-4">
          {cartItems.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-white/20 space-y-4">
              <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center">
                <Trash2 size={32} />
              </div>
              <p className="text-lg font-black uppercase tracking-tighter">
                Tu carrito está vacío
              </p>
              <button
                onClick={toggleCart}
                className="text-red-500 hover:text-red-400 font-medium"
              >
                Ver productos
              </button>
            </div>
          ) : (
            cartItems.map((item) => (
              <div
                key={item.id}
                className="bg-white/5 rounded-2xl p-4 flex gap-4 border border-white/5 hover:border-white/10 transition-all"
              >
                <img
                  src={item.image}
                  alt={item.name}
                  className="w-20 h-20 object-cover rounded-xl bg-black shadow-inner"
                />
                <div className="flex-grow flex flex-col justify-between">
                  <div>
                    <h3 className="text-white font-medium line-clamp-1">
                      {item.name}
                    </h3>
                    <p className="text-red-400 font-bold text-sm">
                      {user ? formatPrice(item.price) : "***"}
                    </p>
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <div className="flex items-center gap-2 bg-white/5 rounded-lg p-1 border border-white/5">
                      <button
                        onClick={() => updateQuantity(item.id, -1)}
                        className="p-1 text-white/40 hover:text-white hover:bg-white/10 rounded"
                        disabled={item.quantity <= 1}
                      >
                        <Minus size={14} />
                      </button>
                      <span className="text-white text-sm font-medium w-4 text-center">
                        {item.quantity}
                      </span>
                      <button
                        onClick={() => updateQuantity(item.id, 1)}
                        className="p-1 text-white/40 hover:text-white hover:bg-white/10 rounded-lg"
                      >
                        <Plus size={14} />
                      </button>
                    </div>
                    <button
                      onClick={() => removeFromCart(item.id)}
                      className="p-2 text-white/20 hover:text-red-500 transition-colors"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {cartItems.length > 0 && (
          <div className="p-6 border-t border-white/10 bg-black/80 backdrop-blur-md space-y-6">
            <div className="flex items-center justify-between text-lg font-bold text-white">
              <span>Total</span>
              <span>{user ? formatPrice(cartTotal) : "***"}</span>
            </div>
            {/* Checkout Button - RED accent */}
            <button
              onClick={handleCheckout}
              className="w-full py-3 bg-red-600 hover:bg-red-500 text-white font-bold rounded-xl flex items-center justify-center gap-2 transition-all shadow-lg shadow-red-600/20 active:scale-[0.98] animate-pulse-red"
            >
              <MessageCircle size={20} />
              Pedir por WhatsApp
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default CartDrawer;
